import {put, call} from 'redux-saga/effects';
import TimeOff from "../../entities/timeOff";
import * as Actions from "../actions/timeOffActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type TimeOffAction = {
    timeOffId: string;
    timeOff: TimeOff;
    timeOffs: TimeOff[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneTimeOff(action: TimeOffAction) {
	const session = tabSession.captureGeneration();
	try {
		const timeOff: TimeOff = yield call(api.findOne, 'timeOff', action.timeOffId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.TIMEOFF_FETCH_ONE_SUCCESS, timeOffId: action.timeOffId, timeOff});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TIMEOFF_FETCH_ONE_FAILURE, timeOffId: action.timeOffId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {TimeOffAction} action
 */
export function* saveTimeOff(action: TimeOffAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ TIMEOFF_SAVE saga', action.timeOff?.id);
    
	if (action.timeOff === undefined || action.timeOff === null) {
		console.error('saveTimeOff received a null');
		throw new Error('saveTimeOff received a null');
	}

	if (!(action.timeOff instanceof TimeOff)) {
		action.timeOff = new TimeOff(action.timeOff);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'timeOff', action.timeOff);
		if (addResult.isErr()) {
			console.error(`⮂ saveTimeOff: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.TIMEOFF_SAVE_FAILURE, timeOff: action.timeOff, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.TIMEOFF_SAVE, timeOff: action.timeOff});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.TIMEOFF_SAVE_FAILURE, timeOff: action.timeOff, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {TimeOffAction} action
 * @param {TimeOff[]} action.timeOffs
 */
export function* saveManyTimeOffs(action: TimeOffAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ TIMEOFF_SAVE_MANY saga', action.timeOffs.map(timeOff => timeOff.id).join(','));
    
    if (!Array.isArray(action.timeOffs)) {
        console.error('saveManyTimeOffs expected an array, received', action.timeOffs);
        throw new Error('saveManyTimeOffs expected an array');
    }

	try {
		const toSave: TimeOff[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.timeOffs.length; i++) {
			const timeOff = action.timeOffs[i];
			timeOff.persisted = false;
			toSave.push(timeOff);
		}

		const addResult = yield call(idb.add, 'timeOff', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyTimeOffs: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.TIMEOFF_SAVE_MANY_FAILURE, timeOffs: action.timeOffs, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.TIMEOFF_SAVE_MANY, timeOffs: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.TIMEOFF_SAVE_MANY_FAILURE, timeOffs: action.timeOffs, status: 500, err});

		console.error(`Error saving ${action.timeOffs.length} timeOffs to database`, err);
	}
}

/**
 * @param {TimeOffAction} action
 */
export function* removeTimeOff(action: TimeOffAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.timeOff === undefined || action.timeOff === null || !(action.timeOff instanceof TimeOff)) {
			console.error('removeTimeOff expected type TimeOff, received', action.timeOff);
			throw new Error('removeTimeOff expected type TimeOff');
		}

	try {
		// Mark as deleted
		action.timeOff.status = 'D';
		action.timeOff.addedOn = newHRTimestamp();
		action.timeOff.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'timeOff', action.timeOff);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeTimeOff: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TIMEOFF_REMOVE_FAILURE, timeOff: action.timeOff, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TIMEOFF_REMOVE, timeOff: action.timeOff});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TIMEOFF_REMOVE_FAILURE, timeOff: action.timeOff, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this TimeOff.
	*
	* @param {TimeOffAction} action
	*/
export function* restoreTimeOff(action: TimeOffAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreTimeOff expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.timeOff === undefined || action.timeOff === null) {
        console.error('restoreTimeOff expected a TimeOff, received null');
        throw new Error('restoreTimeOff expected a TimeOff, received null');
    }
    else if (!(action.timeOff instanceof TimeOff)) action.timeOff = new TimeOff(action.timeOff); 
    
	try {
		// Mark as restored
		action.timeOff.status = 'U';
		action.timeOff.addedOn = newHRTimestamp();
		action.timeOff.addedBy = userId;
		action.timeOff.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'timeOff', action.timeOff);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreTimeOff: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TIMEOFF_RESTORE_FAILURE, timeOff: action.timeOff, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TIMEOFF_RESTORE, timeOff: action.timeOff});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TIMEOFF_RESTORE_FAILURE, timeOff: action.timeOff, status: 500, err});

		console.error('Error restoring from database', err);
	}
}